import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBoardId, useWorkspaceId } from '@trello/id-context';
import { EnterpriseIcon } from '@trello/nachos/icons/enterprise';
import { OrganizationVisibleIcon } from '@trello/nachos/icons/organization-visible';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { PublicIcon } from '@trello/nachos/icons/public';
import { RouterLink } from '@trello/router/router-link';
import { token } from '@trello/theme';
import { boardUrlFromShortLink, getOrganizationUrl } from '@trello/urls';

import { useActionFooterBoardVisibilityFragment } from './ActionFooterBoardVisibilityFragment.generated';
import { useActionFooterOrgVisibilityFragment } from './ActionFooterOrgVisibilityFragment.generated';

import styles from './ActionFooter.less';

export const ActionFooter: FunctionComponent = () => {
  const boardId = useBoardId();
  const workspaceId = useWorkspaceId();

  const { data: boardVisibility } = useActionFooterBoardVisibilityFragment({
    from: { id: boardId },
  });
  const { data: organizationVisibility } = useActionFooterOrgVisibilityFragment(
    {
      from: { id: workspaceId },
    },
  );

  const getVisibilityIcon = (visibility: string | null | undefined) => {
    switch (visibility) {
      case 'public':
        return (
          <PublicIcon
            size="small"
            color={token('color.icon.accent.green', '#008000')}
          />
        );
      case 'private':
        return (
          <PrivateIcon
            size="small"
            color={token('color.icon.accent.red', '#ff0000')}
          />
        );
      case 'enterprise':
        return (
          <EnterpriseIcon
            size="small"
            color={token('color.icon.subtle', '#808080')}
          />
        );
      default:
        return (
          <OrganizationVisibleIcon
            size="small"
            color={token('color.icon.accent.yellow', '#b38600')}
          />
        );
    }
  };

  return (
    <>
      {boardVisibility?.name && (
        <span
          data-testid="member-activity-details"
          className={styles.memberActivityDetails}
        >
          <FormattedMessage
            id="templates.action.member-activity-action-details"
            defaultMessage="• on board <a>{boardName}</a>"
            values={{
              boardName: boardVisibility.name,
              a: (children) => (
                <RouterLink
                  href={boardUrlFromShortLink(
                    boardVisibility?.shortLink,
                    boardVisibility.name,
                  )}
                >
                  {children}
                </RouterLink>
              ),
            }}
            description="Text on link to reply to an attachment"
          />
          <span className={styles.visibilityIcon}>
            {getVisibilityIcon(boardVisibility.prefs?.permissionLevel)}
          </span>
        </span>
      )}
      {organizationVisibility?.name && organizationVisibility.displayName && (
        <span
          data-testid="member-activity-details-workspace"
          className={styles.memberActivityDetails}
        >
          <FormattedMessage
            id="templates.action.member-activity-action-details-workspace"
            defaultMessage="• on Workspace <a>{workspaceName}</a>"
            values={{
              workspaceName: organizationVisibility.displayName,
              a: (children) => (
                <RouterLink
                  href={getOrganizationUrl(organizationVisibility.name)}
                >
                  {children}
                </RouterLink>
              ),
            }}
            description="Text on link to reply to an attachment"
          />
          <span className={styles.visibilityIcon}>
            {getVisibilityIcon(organizationVisibility.prefs?.permissionLevel)}
          </span>
        </span>
      )}
    </>
  );
};
